import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import Card from '@/components/UI/Card';
import DataTable from '@/components/UI/DataTable';
import SegmentedControl from '@/components/UI/SegmentedControl';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { buildCombinationRows, buildExitCandidateRows, buildOptionStrategyRows, type BreakdownRow } from './backtestBreakdownRows';
import type { BacktestParameterAnalysis, StrategyCoverage } from './backtestStatusTypes';

interface BacktestBreakdownPanelProps {
    analysis: BacktestParameterAnalysis | undefined;
    isLoading: boolean;
    strategyId: number;
    populationProgress?: StrategyCoverage['populationProgress'];
    exitCandidateLabelById: Map<number, string>;
    optionStrategyNameById: Map<number, string>;
}

type Dimension = 'exit' | 'option' | 'combination';

const gainClass = (value: number): string => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';

    return 'text-zinc-300';
};

/** Right-aligns both the cell and the header label, which DataTable lays out inside a flex row. */
const numericColumnMeta = { className: 'text-right font-mono', headerClassName: '[&>div]:justify-end' };

const HeaderLabel = ({ label, description }: { label: string; description: string }) => (
    <span className="cursor-help" title={description}>
        {label}
    </span>
);

const BreakdownTable = ({ rows }: { rows: BreakdownRow[] }) => {
    const maxCount = rows.reduce((max, row) => Math.max(max, row.backtestCount), 0);
    // Flag rows whose sample is a small fraction of the largest, so a thin, outlier-sensitive average
    // (e.g. a discovery variation over a handful of entities) is not compared as equal to a full-universe row.
    const lowSampleThreshold = maxCount >= 20 ? maxCount * 0.25 : 0;

    const columns = useMemo<ColumnDef<BreakdownRow>[]>(
        () => [
            {
                accessorKey: 'label',
                header: 'Name',
                // Bounded so long exit-rule and multi-leg labels wrap instead of squeezing the metric columns.
                size: 260,
                minSize: 180,
                maxSize: 320,
                cell: (info) => {
                    const row = info.row.original;

                    // Both lines wrap rather than truncate: the option strategy legs are often the only thing
                    // separating two rows that share an exit rule, and an exit label carries its stop/target inline.
                    return (
                        <>
                            <div className="break-words" title={row.labelTitle ?? row.label}>
                                {row.label}
                            </div>
                            {row.sublabel && (
                                <div className="break-words text-xs text-zinc-500" title={row.sublabel}>
                                    {row.sublabel}
                                </div>
                            )}
                        </>
                    );
                }
            },
            {
                accessorKey: 'backtestCount',
                header: 'Backtests',
                size: 140,
                meta: { headerClassName: '[&>div]:justify-end' },
                cell: (info) => {
                    const { backtestCount } = info.row.original;

                    return (
                        <div className="flex items-center justify-end gap-2">
                            {backtestCount < lowSampleThreshold && (
                                <FaExclamationTriangle
                                    aria-hidden="true"
                                    className="text-amber-400"
                                    title={`Small sample — averaged over only ${backtestCount} backtests. Outlier-sensitive and not comparable to larger rows.`}
                                />
                            )}
                            <div className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-zinc-800 sm:block">
                                <div
                                    className="h-full bg-cyan-500"
                                    style={{ width: `${maxCount > 0 ? Math.round((backtestCount / maxCount) * 100) : 0}%` }}
                                />
                            </div>
                            <span className="font-mono">{backtestCount}</span>
                        </div>
                    );
                }
            },
            {
                accessorKey: 'totalTrades',
                header: 'Trades',
                size: 90,
                meta: numericColumnMeta,
                cell: (info) => (info.row.original.totalTrades > 0 ? info.row.original.totalTrades.toLocaleString() : '—')
            },
            {
                accessorKey: 'tradeWinRatePercent',
                header: () => (
                    <HeaderLabel
                        description="Winning entered trades ÷ entered trades, using the same pooled trade rows as average trade return"
                        label="Win rate"
                    />
                ),
                size: 100,
                meta: numericColumnMeta,
                cell: (info) => `${info.row.original.tradeWinRatePercent.toFixed(1)}%`
            },
            {
                accessorKey: 'avgTradeReturnPercent',
                header: () => (
                    <HeaderLabel
                        description="Average return per entered trade, including both winning and losing trades"
                        label="Avg trade return"
                    />
                ),
                size: 130,
                meta: numericColumnMeta,
                cell: (info) => (
                    <span className={gainClass(info.row.original.avgTradeReturnPercent)}>
                        {info.row.original.avgTradeReturnPercent.toFixed(2)}%
                    </span>
                )
            },
            {
                accessorKey: 'avgCumulativeReturnPercent',
                header: () => (
                    <HeaderLabel
                        description="Average cumulative return per standalone backtest over the full window (net profit ÷ $100k starting balance)"
                        label="Avg cumulative return"
                    />
                ),
                size: 150,
                meta: numericColumnMeta,
                cell: (info) => (
                    <span className={gainClass(info.row.original.avgCumulativeReturnPercent)}>
                        {info.row.original.avgCumulativeReturnPercent.toFixed(2)}%
                    </span>
                )
            }
        ],
        [lowSampleThreshold, maxCount]
    );

    return <DataTable columns={columns} data={rows} pagination={{ enabled: true, pageSize: 15 }} search={false} striped={false} />;
};

const BacktestBreakdownPanel = ({
    analysis,
    isLoading,
    strategyId,
    populationProgress,
    exitCandidateLabelById,
    optionStrategyNameById
}: BacktestBreakdownPanelProps) => {
    const hasOptionStrategies = (analysis?.byOptionStrategy.length ?? 0) > 0;
    const [dimension, setDimension] = useState<Dimension>('exit');
    // Without option strategies a combination is just its exit rule, so both extra dimensions collapse back to it.
    const activeDimension = dimension !== 'exit' && !hasOptionStrategies ? 'exit' : dimension;

    const exitRows = useMemo(() => buildExitCandidateRows(analysis, exitCandidateLabelById), [analysis, exitCandidateLabelById]);

    const optionRows = useMemo(() => buildOptionStrategyRows(analysis, optionStrategyNameById), [analysis, optionStrategyNameById]);

    const combinationRows = useMemo(
        () => buildCombinationRows(analysis, exitCandidateLabelById, optionStrategyNameById),
        [analysis, exitCandidateLabelById, optionStrategyNameById]
    );

    const rows = (() => {
        if (activeDimension === 'option') return optionRows;
        if (activeDimension === 'combination') return combinationRows;

        return exitRows;
    })();

    return (
        <Card>
            <div className="p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h3 className="text-lg font-semibold text-zinc-100">Current-version performance</h3>
                        {populationProgress && populationProgress.expectedBacktests > 0 && (
                            <div className="mt-0.5 text-xs text-zinc-500">
                                Population {populationProgress.completionPercent ?? 0}% complete ({populationProgress.completedBacktests} of{' '}
                                {populationProgress.expectedBacktests} required backtests)
                            </div>
                        )}
                    </div>
                    <Link
                        className="text-xs text-cyan-400 hover:text-cyan-300"
                        state={{ strategyId }}
                        to="/tools/backtestParameterAnalysis"
                    >
                        Full analysis →
                    </Link>
                </div>

                {isLoading ? (
                    <div className="py-8">
                        <DataLoadingSpinner text="Analyzing backtests..." />
                    </div>
                ) : !analysis || analysis.summary.totalBacktests === 0 ? (
                    <div className="py-8 text-center text-sm text-zinc-500">
                        No current-version backtests with entered trades to summarize yet.
                    </div>
                ) : (
                    <>
                        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                            <span>
                                <span className="font-mono text-zinc-300">{analysis.summary.totalBacktests}</span> analyzed backtests
                            </span>
                            <span>
                                <span className="font-mono text-zinc-300">
                                    {analysis.summary.totalTrades > 0 ? analysis.summary.totalTrades.toLocaleString() : '—'}
                                </span>{' '}
                                trades
                            </span>
                            <span>
                                <span className="font-mono text-zinc-300">{analysis.summary.uniqueCombinations}</span> combinations
                            </span>
                            <span>
                                <span className="font-mono text-zinc-300">{analysis.summary.uniqueExitCandidates}</span> exit rules
                            </span>
                            <span>
                                <span className="font-mono text-zinc-300">{analysis.summary.uniqueOptionStrategies}</span> option strategies
                            </span>
                        </div>

                        {hasOptionStrategies && (
                            <div className="mb-3">
                                <SegmentedControl
                                    options={[
                                        { value: 'exit', label: 'By exit rule' },
                                        { value: 'option', label: 'By option strategy' },
                                        { value: 'combination', label: 'By combination' }
                                    ]}
                                    value={activeDimension}
                                    onChange={setDimension}
                                />
                            </div>
                        )}

                        {rows.length > 0 ? (
                            // Remount per dimension so a column sort from one tab does not carry into another
                            // tab that has its own default ordering.
                            <BreakdownTable key={activeDimension} rows={rows} />
                        ) : (
                            <div className="py-6 text-center text-sm text-zinc-500">No data for this dimension.</div>
                        )}
                        <div className="mt-3 text-xs text-zinc-600">
                            {activeDimension === 'combination'
                                ? 'Each row is one exit rule paired with its option strategy legs, ranked by avg cumulative return (best first) until you sort by another column. '
                                : 'Rows start ranked by backtest count; click any column header to sort. '}
                            Win rate and avg trade return pool the same entered trades, including winners and losers. Avg cumulative return
                            is each standalone backtest&rsquo;s net profit ÷ $100k starting balance over the full window, averaged across
                            matching backtests. Rows with far fewer backtests (flagged ⚠) are outlier-sensitive and not directly comparable.
                        </div>
                    </>
                )}
            </div>
        </Card>
    );
};

export default BacktestBreakdownPanel;
